import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PlanAwareLink from '../../components/billing/PlanAwareLink';
import PlanTierLabel from '../../components/billing/PlanTierLabel';
import PlanUpgradeNotice from '../../components/billing/PlanUpgradeNotice';
import { FEATURE } from '../../config/planFeatures';
import { useUserPlan } from '../../hooks/useUserPlan';
import {
  LuActivity,
  LuBell,
  LuBookOpen,
  LuClipboardList,
  LuCrown,
  LuInbox,
  LuLayoutDashboard,
  LuListChecks,
  LuSparkles,
  LuSwords,
  LuTrendingUp,
  LuUser,
  LuUsers,
  LuUserPlus,
} from 'react-icons/lu';
import AuthContext from '../../components/shared/AuthContext';
import TeamNotificationsPanel from '../../components/teams/TeamNotificationsPanel';
import { useTeamContext } from '../../context/TeamContext';

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="teams-kpi">
      <p className="teams-kpi-label">
        {Icon && <Icon aria-hidden className="teams-kpi-icon" />}
        {label}
      </p>
      <p className="teams-kpi-value">{value}</p>
    </div>
  );
}

function QuickLink({ to, icon: Icon, children, planFeature }) {
  const { canUseFeature } = useUserPlan();
  const locked = planFeature && !canUseFeature(planFeature);
  const Cmp = locked ? PlanAwareLink : Link;
  return (
    <Cmp to={to} className="teams-overview-link">
      {Icon && <Icon aria-hidden />}
      <span>{children}</span>
      {locked && <PlanTierLabel tier="Team" />}
    </Cmp>
  );
}

function Recommendations({ title, icon: TitleIcon, tips }) {
  if (!tips?.length) return null;
  return (
    <div className="teams-card">
      <h2 className="teams-section-title">
        {TitleIcon && <TitleIcon aria-hidden />}
        {title}
      </h2>
      <ul className="teams-list">
        {tips.map((tip) => (
          <li key={tip}>{tip}</li>
        ))}
      </ul>
    </div>
  );
}

function ManagerOverview({ orgId, dash, onNotifyUpdated }) {
  const base = `/teams/${orgId}`;
  const title = dash.isOwner ? 'Owner overview' : 'Team management overview';

  return (
    <>
      <div className="teams-overview-intro">
        <h2 className="teams-section-title teams-overview-heading">
          {dash.isOwner ? <LuCrown aria-hidden /> : <LuLayoutDashboard aria-hidden />}
          {title}
        </h2>
        <p className="teams-sub">
          {dash.isOwner
            ? 'Workspace health, engagement, and pending invites across your team.'
            : 'Monitor participation, assignments, and invites for this workspace.'}
        </p>
      </div>

      <div className="teams-kpi-grid">
        <Kpi icon={LuUsers} label="Members" value={dash.memberCount} />
        <Kpi icon={LuActivity} label="Active this week" value={dash.activeThisWeek} />
        <Kpi icon={LuTrendingUp} label="Team learning score" value={dash.teamLearningScore} />
        <Kpi icon={LuSparkles} label="Team XP" value={dash.teamXp?.toLocaleString?.() ?? dash.teamXp} />
        <Kpi icon={LuClipboardList} label="Open assignments" value={dash.openAssignments} />
        <Kpi icon={LuSwords} label="Active challenges" value={dash.activeChallenges} />
        {(dash.orgPendingAcceptances ?? 0) > 0 && (
          <Kpi icon={LuBell} label="Awaiting acceptance" value={dash.orgPendingAcceptances} />
        )}
      </div>

      <div className="teams-card teams-overview-actions">
        <h2 className="teams-section-title">
          <LuUserPlus aria-hidden />
          Quick actions
        </h2>
        <div className="teams-overview-link-grid">
          <QuickLink to={`${base}/members`} icon={LuUsers}>Invite & manage members</QuickLink>
          <QuickLink to={`${base}/assignments`} icon={LuClipboardList}>Create assignment</QuickLink>
          <QuickLink to={`${base}/challenges`} icon={LuSwords}>Launch challenge</QuickLink>
          <QuickLink to={`${base}/groups`} icon={LuUsers}>Organize groups</QuickLink>
        </div>
      </div>

      <TeamNotificationsPanel
        orgId={orgId}
        notifications={dash.notifications}
        onUpdated={onNotifyUpdated}
      />

      <Recommendations title="Recommendations" icon={LuTrendingUp} tips={dash.recommendations} />
    </>
  );
}

function MemberOverview({ orgId, dash, onNotifyUpdated }) {
  const base = `/teams/${orgId}`;
  const pending = dash.myPendingRequests ?? dash.notifications?.pendingCount ?? 0;

  return (
    <>
      <div className="teams-overview-intro">
        <h2 className="teams-section-title teams-overview-heading">
          <LuUser aria-hidden />
          Your overview
        </h2>
        <p className="teams-sub">
          Your progress and pending invites. Assignment reads count only after you accept on Requests.
        </p>
      </div>

      <TeamNotificationsPanel
        orgId={orgId}
        notifications={dash.notifications}
        onUpdated={onNotifyUpdated}
      />

      <div className="teams-kpi-grid">
        <Kpi icon={LuTrendingUp} label="Your learning score" value={dash.myLearningScore ?? 0} />
        <Kpi icon={LuSparkles} label="Your XP" value={dash.myLifetimeXp?.toLocaleString?.() ?? dash.myLifetimeXp ?? 0} />
        <Kpi icon={LuInbox} label="Pending requests" value={pending} />
        <Kpi icon={LuListChecks} label="Your open assignments" value={dash.myOpenAssignments ?? 0} />
      </div>

      <div className="teams-card teams-overview-actions">
        <h2 className="teams-section-title">
          <LuBookOpen aria-hidden />
          Get started
        </h2>
        <div className="teams-overview-link-grid">
          <QuickLink to={`${base}/requests`} icon={LuInbox}>Review requests</QuickLink>
          <QuickLink to={`${base}/assignments`} icon={LuClipboardList}>Your assignments</QuickLink>
          <QuickLink
            to={`${base}/leaderboard`}
            icon={LuTrendingUp}
            planFeature={FEATURE.TEAM_LEADERBOARD_ANALYTICS}
          >
            Leaderboard
          </QuickLink>
          <QuickLink to="/library" icon={LuBookOpen}>Library — mark read</QuickLink>
        </div>
      </div>

      <Recommendations title="For you" icon={LuListChecks} tips={dash.recommendations} />
    </>
  );
}

export default function TeamOverviewPage() {
  const { orgId } = useParams();
  const { apiClient } = useContext(AuthContext);
  const { activeOrg } = useTeamContext();
  const { planReady, canUseFeature } = useUserPlan();
  const [dash, setDash] = useState(null);
  const [error, setError] = useState('');
  const [dashTick, setDashTick] = useState(0);
  const canTeamAnalytics = canUseFeature(FEATURE.TEAM_LEADERBOARD_ANALYTICS);

  useEffect(() => {
    if (!planReady) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/api/organizations/${orgId}/dashboard`);
        if (!cancelled) setDash(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.response?.data?.detail || 'Dashboard unavailable.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [apiClient, orgId, dashTick, planReady]);

  if (error) return <div className="teams-error" role="alert">{error}</div>;
  if (!dash) return <p className="teams-sub">Loading dashboard…</p>;

  const role = (dash.viewerRole || activeOrg?.role || '').toUpperCase();
  const isManager = dash.canViewManagerAnalytics
    ?? (role === 'OWNER' || role === 'ADMIN' || role === 'MANAGER');

  if (isManager && planReady && !canTeamAnalytics) {
    return (
      <>
        <PlanUpgradeNotice feature={FEATURE.TEAM_LEADERBOARD_ANALYTICS} embedded />
        <MemberOverview orgId={orgId} dash={dash} onNotifyUpdated={() => setDashTick((t) => t + 1)} />
      </>
    );
  }

  return isManager ? (
    <ManagerOverview orgId={orgId} dash={dash} onNotifyUpdated={() => setDashTick((t) => t + 1)} />
  ) : (
    <MemberOverview orgId={orgId} dash={dash} onNotifyUpdated={() => setDashTick((t) => t + 1)} />
  );
}
